import React, { useEffect, useState } from 'react';
import { Users, Star, TrendingUp, Tag, DollarSign, ArrowUp, ArrowDown } from 'lucide-react';
import { analyticsAPI } from '../../services/api';
import { PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await analyticsAPI.getDashboard();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 pt-12 lg:pt-0">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="skeleton h-32 rounded-2xl" />)}
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="skeleton h-80 rounded-2xl" />
          <div className="skeleton h-80 rounded-2xl" />
        </div>
      </div>
    );
  }

  const tierData = stats?.tier_distribution ? [
    { name: 'Bronze', value: stats.tier_distribution.Bronze || 0, color: '#CD7F32' },
    { name: 'Silver', value: stats.tier_distribution.Silver || 0, color: '#C0C0C0' },
    { name: 'Gold', value: stats.tier_distribution.Gold || 0, color: '#FFD700' },
    { name: 'Platinum', value: stats.tier_distribution.Platinum || 0, color: '#E5E4E2' },
  ].filter(d => d.value > 0) : [];

  const pointsChange = stats?.today?.points_awarded && stats?.yesterday?.points_awarded
    ? ((stats.today.points_awarded - stats.yesterday.points_awarded) / (stats.yesterday.points_awarded || 1) * 100).toFixed(0)
    : 0;

  const revenueChange = stats?.today?.revenue && stats?.yesterday?.revenue
    ? ((stats.today.revenue - stats.yesterday.revenue) / (stats.yesterday.revenue || 1) * 100).toFixed(0)
    : 0;

  return (
    <div className="space-y-6 pt-12 lg:pt-0 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl text-[#4A154B]">Dashboard</h1>
        <p className="text-gray-600">Welcome to Salon O Admin Portal</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass rounded-2xl p-5 card-hover" data-testid="total-customers-card">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-[#4A154B] rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-1">Total Customers</p>
          <p className="font-serif text-3xl text-[#4A154B]">{stats?.total_customers || 0}</p>
        </div>

        <div className="glass rounded-2xl p-5 card-hover" data-testid="today-points-card">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-[#C9A689] rounded-xl flex items-center justify-center">
              <Star className="w-5 h-5 text-white" />
            </div>
            {pointsChange !== 0 && (
              <span className={`flex items-center text-sm ${Number(pointsChange) >= 0 ? 'text-[#8FBC8F]' : 'text-[#C74545]'}`}>
                {Number(pointsChange) >= 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                {Math.abs(Number(pointsChange))}%
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mb-1">Points Today</p>
          <p className="font-serif text-3xl text-[#4A154B]">{stats?.today?.points_awarded || 0}</p>
        </div>

        <div className="glass rounded-2xl p-5 card-hover" data-testid="today-revenue-card">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-[#8FBC8F] rounded-xl flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            {revenueChange !== 0 && (
              <span className={`flex items-center text-sm ${Number(revenueChange) >= 0 ? 'text-[#8FBC8F]' : 'text-[#C74545]'}`}>
                {Number(revenueChange) >= 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                {Math.abs(Number(revenueChange))}%
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mb-1">Revenue Today</p>
          <p className="font-serif text-3xl text-[#4A154B]">${(stats?.today?.revenue || 0).toFixed(0)}</p>
        </div>

        <div className="glass rounded-2xl p-5 card-hover" data-testid="active-promos-card">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-[#2C2C2C] rounded-xl flex items-center justify-center">
              <Tag className="w-5 h-5 text-white" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-1">Active Promotions</p>
          <p className="font-serif text-3xl text-[#4A154B]">{stats?.active_promotions || 0}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Tier Distribution */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-serif text-xl text-[#4A154B] mb-4">Customer Tiers</h3>
          {tierData.length > 0 ? (
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={tierData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {tierData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-12">No customer data</p>
          )}
          <div className="flex justify-center gap-4 mt-4">
            {tierData.map((entry) => (
              <div key={entry.name} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-sm text-gray-600">{entry.name} ({entry.value})</span>
              </div>
            ))}
          </div>
        </div>

        {/* Points Trend */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-serif text-xl text-[#4A154B] mb-4">Points Trend (30 Days)</h3>
          {stats?.points_trend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={stats.points_trend}>
                <defs>
                  <linearGradient id="colorEarned" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8FBC8F" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8FBC8F" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorRedeemed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C9A689" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#C9A689" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Area type="monotone" dataKey="earned" stroke="#8FBC8F" fillOpacity={1} fill="url(#colorEarned)" />
                <Area type="monotone" dataKey="redeemed" stroke="#C9A689" fillOpacity={1} fill="url(#colorRedeemed)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-12">No trend data</p>
          )}
          <div className="flex justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#8FBC8F]" />
              <span className="text-sm text-gray-600">Earned</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#C9A689]" />
              <span className="text-sm text-gray-600">Redeemed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="glass rounded-2xl p-6">
        <h3 className="font-serif text-xl text-[#4A154B] mb-4">Recent Transactions</h3>
        {stats?.recent_transactions?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#F5E6D3]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Service/Reward</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Points</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Amount</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_transactions.map((t) => (
                  <tr key={t.id} className="border-b border-[#F5E6D3]/50 last:border-0">
                    <td className="py-3 px-4">
                      <span className={`badge ${t.type === 'earned' ? 'badge-success' : 'badge-warning'}`}>
                        {t.type}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[#4A154B]">
                      {t.service_name || t.reward_name || '-'}
                    </td>
                    <td className={`py-3 px-4 text-right font-medium ${t.type === 'earned' ? 'text-[#8FBC8F]' : 'text-[#C74545]'}`}>
                      {t.type === 'earned' ? '+' : ''}{t.points}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-600">
                      {t.amount ? `$${t.amount.toFixed(2)}` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No recent transactions</p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
